const UPDATED_KEY = "reading-bee:app-updated";
const RELOAD_KEY = "reading-bee:sw-reload";

export type AppUpdatePhase = "idle" | "updating" | "updated" | "leaving";

export const updatePercent = (loaded: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((loaded / total) * 100));
};

class AppUpdateBus extends EventTarget {
  phase: AppUpdatePhase = "idle";
  loaded = 0;
  total = 0;

  get percent(): number {
    return updatePercent(this.loaded, this.total);
  }

  setPhase(phase: AppUpdatePhase): void {
    if (this.phase === phase) return;
    this.phase = phase;
    if (phase === "idle") {
      this.loaded = 0;
      this.total = 0;
    }
    this.dispatchEvent(new Event("change"));
  }

  setProgress(loaded: number, total: number): void {
    this.loaded = loaded;
    this.total = total;
    if (this.phase !== "updating") this.phase = "updating";
    this.dispatchEvent(new Event("change"));
  }
}

export const appUpdate = new AppUpdateBus();

const watchWorker = (worker: ServiceWorker | null): void => {
  if (!worker || !navigator.serviceWorker.controller) return;
  const sync = (): void => {
    if (worker.state === "redundant") {
      if (appUpdate.phase === "updating") appUpdate.setPhase("idle");
      return;
    }
    if (worker.state === "installing" || worker.state === "installed" || worker.state === "activating") {
      appUpdate.setPhase("updating");
    }
  };
  sync();
  worker.addEventListener("statechange", sync);
};

const markUpdatedAndReload = (): void => {
  if (sessionStorage.getItem(RELOAD_KEY) === "1") return;
  sessionStorage.setItem(RELOAD_KEY, "1");
  sessionStorage.setItem(UPDATED_KEY, "1");
  window.location.reload();
};

const revealUpdated = (): void => {
  if (sessionStorage.getItem(UPDATED_KEY) !== "1") return;
  sessionStorage.removeItem(UPDATED_KEY);
  appUpdate.setPhase("updated");
  window.setTimeout(() => {
    appUpdate.setPhase("leaving");
    window.setTimeout(() => {
      appUpdate.setPhase("idle");
    }, 280);
  }, 2000);
};

const onWorkerMessage = (event: MessageEvent<{ type?: string; loaded?: number; total?: number }>): void => {
  if (!navigator.serviceWorker.controller) return;
  const type = event.data?.type;
  if (type !== "reading-bee:updating" && type !== "reading-bee:progress") return;
  appUpdate.setPhase("updating");
  if (typeof event.data?.loaded === "number" && typeof event.data.total === "number") {
    appUpdate.setProgress(event.data.loaded, event.data.total);
  }
};

export const registerServiceWorker = (): void => {
  if (!("serviceWorker" in navigator)) return;
  sessionStorage.removeItem(RELOAD_KEY);
  revealUpdated();
  void navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((registration) => {
    const hadController = Boolean(navigator.serviceWorker.controller);
    watchWorker(registration.installing);
    watchWorker(registration.waiting);
    registration.addEventListener("updatefound", () => {
      watchWorker(registration.installing);
    });
    navigator.serviceWorker.addEventListener("message", onWorkerMessage);
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadController) return;
      markUpdatedAndReload();
    });
    const check = (): void => {
      void registration.update();
    };
    check();
    window.setInterval(check, 60_000);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });
  });
};
